/**
 * Root application component for the triage dashboard SPA.
 *
 * Wires together the three moving parts of the frontend:
 *
 * 1. **TanStack Query `QueryClient`** — the global server-state cache
 *    used by every `useQuery` call in the app. Configured once here
 *    with sensible defaults (30-second stale time, 3 retries with
 *    backoff, no retries on 401/403).
 *
 * 2. **React Router `BrowserRouter`** — client-side routing. Because
 *    the triage dashboard is mounted at the root of a dedicated
 *    hostname (`dashboard.staging.triage.expep.name`), we do NOT set
 *    a `basename` (unlike dk2 which uses `basename="/dashboard"`).
 *    URLs stay clean: `/`, `/analyses`, `/login`.
 *
 * 3. **Route table** — Wave 12 final shape:
 *    - `/login`                      — unauthenticated, renders `<LoginPage>`
 *    - `/`                           — authenticated, renders `<HomePage>` (the menu)
 *    - `/analyses`                   — authenticated, the list (existing page)
 *    - `/analyses/:uuid`             — authenticated, full detail (Wave 11)
 *    - `/bundles`                    — authenticated, bundle_lifecycle list (Wave 11)
 *    - `/bundles/:uuid`              — authenticated, live pipeline strip (Wave 11)
 *    - `/duplicates/:canonicalUuid`  — authenticated, dedup-family detail (Wave 11)
 *    - `/trends`                     — authenticated, recurring-family view (Wave 11)
 *    - `/activity`                   — authenticated, renamed from `/status` (Wave 12)
 *    - `/admin`                      — authenticated + admin-gated (Wave 11)
 *    - `/status`                     — 301-style client redirect to `/activity`
 *    - everything else               → redirect to `/`
 *
 *    Authenticated routes are wrapped in `<AuthGuard>` so an
 *    unauthenticated user hitting `/analyses` directly gets bounced
 *    to `/login` before any data fetch runs. The guard is purely
 *    UX — the backend enforces the session cookie on every
 *    `/api/*` call independently.
 *
 * # Code-splitting (Wave 12)
 *
 * Every authenticated page except `Home` is loaded via
 * `React.lazy` so the initial bundle stays small. Home is eager
 * because it's the landing target after login — lazy-loading the
 * landing page would add a network round-trip to every fresh
 * session. Login is also eager for the same reason.
 *
 * Each lazy page resolves to its own JS chunk (Vite names them
 * `assets/<PageName>-<hash>.js`) which is fetched on first
 * navigation. Per Appendix D.16 the per-chunk target is < 60 KB
 * gzipped; the combined authenticated-chunk total target is
 * < 600 KB gzipped. Splitting drops the initial bundle from
 * ~500 KB raw to ~200 KB raw (measured post-split) with the rest
 * deferred until the user navigates.
 *
 * Suspense fallback is a small centered "Loading…" indicator —
 * same shape as the AuthGuard's fallback so transitions between
 * "checking session" and "loading page code" feel continuous.
 *
 * # Why no shared layout component
 *
 * Ten authenticated pages today, each rendering its own header +
 * main content. A shared `<PageShell>` would centralize the
 * header but the page bodies are each substantial enough that
 * the layout is more content than chrome. Revisit when we add
 * 15+ pages OR when every page starts growing the same side
 * navigation (neither is imminent).
 *
 * # SSR / hydration note
 *
 * This is a pure client-rendered SPA — Vite builds static HTML +
 * JS, axum serves the HTML shell, React hydrates from scratch. No
 * SSR, no Next.js, no streaming. The initial paint is quick
 * because the initial bundle is small (per-route chunks load on
 * demand) and the first fetch is a tiny JSON from the same origin.
 */

import { lazy, Suspense, useEffect } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigationType,
} from "react-router";

import { AuthGuard } from "@/lib/auth";
import { titleForPath } from "@/lib/page-title";
import { useTrackPageView } from "@/lib/tracking";
import { ToastProvider } from "@/lib/toast";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { TimeZoneRerender } from "@/components/TimeZoneRerender";
import { UpdatingBanner } from "@/components/UpdatingBanner";
// 2026-05-01 late-evening operator decision — keyboard shortcuts
// are NOT part of this dashboard's UX. Operators use mouse +
// URL-shareable filter state + Slack links; keyboard power-user
// navigation doesn't match the actual interaction pattern.
//
// Evolution of this decision:
//   - 2026-05-01 morning (commit `00df3a8`): full
//     KeyboardShortcutsProvider + `useRowKeyboardNav` +
//     cheatsheet modal infrastructure built (~650 LOC).
//   - 2026-05-01 noon (commit `86127b1`): provider unmounted
//     because the cheatsheet advertised six shortcuts with zero
//     wired — broken-promise UX. The infra file stayed on disk
//     for a planned future wiring session.
//   - 2026-05-01 late evening: wiring attempt started, then
//     halted + `dashboard/src/lib/keyboard.tsx` was DELETED in
//     the same session per operator directive: "no one will use
//     them." The file is gone; this import block is the
//     load-bearing breadcrumb so future contributors don't
//     reinvent the same rejected feature.
//
// The rule is now documented in `CONTRIBUTING.md` §"Rule: do
// NOT add keyboard shortcuts to the dashboard" (parallel to the
// existing §"Rule: do NOT add new slash commands"). A future
// agent tempted to add `j`/`k`/`?` navigation should read that
// rule first and confirm operator authorization before
// proceeding.
import { LoginPage } from "@/pages/Login";
import { HomePage } from "@/pages/Home";

// ─── Lazy-loaded authenticated pages ────────────────────────────────────────
//
// Every authenticated page except Home is loaded via React.lazy so
// the initial bundle stays small. The import() is resolved on
// first navigation; subsequent navigations hit the module cache
// and paint instantly.
//
// Note the `.then(m => ({default: m.XyzPage}))` pattern — each
// page exports a named component (matching our project convention
// of explicit named exports over default exports), and React.lazy
// requires a default export. The inline rewrite is cheaper than
// adding a `default export` to every page file, and keeps the
// named-export convention intact at the module boundary.

// 2026-05-01: AnalysesPage was removed per operator directive
// ("Recent Analyses page is redundant with /bundles + /trends and
// just a source of confusion for users"). The `/analyses/:uuid`
// detail route stays — Slack links, bundles-page row clicks, and
// the /duplicates drill-through all point at it. Only the LIST
// page + the `/analyses` route + the TopNav entry went away.
// The backend `GET /api/analyses` endpoint is left in place for
// now as dead code; follow-up commit can drop it cleanly.
const ActivityPage = lazy(() =>
  import("@/pages/Activity").then((m) => ({ default: m.ActivityPage })),
);
// Galley #33621 (Chris #33560): search what analyses found, beside the Bundles search.
const SearchFindingsPage = lazy(() =>
  import("@/pages/SearchFindings").then((m) => ({ default: m.SearchFindingsPage })),
);
const AdminPage = lazy(() =>
  import("@/pages/Admin").then((m) => ({ default: m.AdminPage })),
);
// 2026-04-29 observability: admin-gated LLM charts page.
// Lazy-loaded alongside AdminPage because (a) the d3 chart code
// is ~30 KB gzipped and most operators never visit this page,
// and (b) a non-admin user who navigates here sees the
// ForbiddenPanel after the network round-trip — we don't want
// to pay the bundle cost on every page load for that handful
// of visits.
const AdminLlmPage = lazy(() =>
  import("@/pages/AdminLlm").then((m) => ({ default: m.AdminLlmPage })),
);
// 2026-04-29 TOP-OF-QUEUE #2: admin-gated Usersnap ingest
// observability page. Lazy-loaded alongside AdminLlmPage for the
// same rationale — most operators never visit this page, and a
// non-admin user sees the ForbiddenPanel after a round-trip so
// the bundle cost isn't worth paying on every page load.
const AdminMcpPage = lazy(() =>
  import("@/pages/AdminMcp").then((m) => ({
    default: m.AdminMcpPage,
  })),
);

// The MCP guide (Galley #14395): a page beside /admin/mcp, linked from
// the Hand off dialog. Same AuthGuard-only gate as /admin/mcp.
const McpGuidePage = lazy(() =>
  import("@/pages/McpGuide").then((m) => ({
    default: m.McpGuidePage,
  })),
);

const AdminUsersnapPage = lazy(() =>
  import("@/pages/AdminUsersnap").then((m) => ({
    default: m.AdminUsersnapPage,
  })),
);
// 2026-05-06 Priority 3 Phase 2: admin-gated dashboard-usage
// analytics page. Lazy-loaded alongside AdminLlmPage +
// AdminUsersnapPage for the same rationale — non-admin users
// see the ForbiddenPanel after a round-trip so the bundle cost
// isn't worth paying on every page load.
const AdminPageViewsPage = lazy(() =>
  import("@/pages/AdminPageViews").then((m) => ({
    default: m.AdminPageViewsPage,
  })),
);
const BundlesPage = lazy(() =>
  import("@/pages/Bundles").then((m) => ({ default: m.BundlesPage })),
);
const BundleDetailPage = lazy(() =>
  import("@/pages/BundleDetail").then((m) => ({
    default: m.BundleDetailPage,
  })),
);
const AnalysisDetailPage = lazy(() =>
  import("@/pages/AnalysisDetail").then((m) => ({
    default: m.AnalysisDetailPage,
  })),
);

// Feature B (2026-05-01) — dedicated LLM conversation viewer at
// `/analyses/:uuid/conversation`. Lazy-loaded so the chunk only
// downloads when an operator actually clicks through from the
// AnalysisDetail "View LLM conversation →" CTA. Renders the
// interleaved chronological stream of user / assistant / text /
// thinking / tool_use / tool_result blocks read from the
// `conversation.json` S3 artifact via the auth-tracked streaming
// endpoint `GET /api/analyses/:uuid/conversation`.
const ConversationPage = lazy(() =>
  import("@/pages/Conversation").then((m) => ({
    default: m.ConversationPage,
  })),
);
const DuplicatesPage = lazy(() =>
  import("@/pages/Duplicates").then((m) => ({ default: m.DuplicatesPage })),
);
// Galley #34714 — Recurring fixes, a new page in the Fixes menu.
const RecurringFixesPage = lazy(() =>
  import("@/pages/RecurringFixes").then((m) => ({ default: m.RecurringFixesPage })),
);
// Galley #36384 — Problems, a new page at the end of the Fixes menu.
const FixProblemsPage = lazy(() =>
  import("@/pages/FixProblems").then((m) => ({ default: m.FixProblemsPage })),
);
const FixProblemDetailPage = lazy(() =>
  import("@/pages/FixProblemDetail").then((m) => ({ default: m.FixProblemDetailPage })),
);
// TRIAG-312 — commit pre-flight review surface.
const CommitsPage = lazy(() =>
  import("@/pages/Commits").then((m) => ({ default: m.CommitsPage })),
);
const CommitDetailPage = lazy(() =>
  import("@/pages/CommitDetail").then((m) => ({ default: m.CommitDetailPage })),
);
const TrendsPage = lazy(() =>
  import("@/pages/Trends").then((m) => ({ default: m.TrendsPage })),
);
const TrendAlertsPage = lazy(() =>
  import("@/pages/TrendAlerts").then((m) => ({ default: m.TrendAlertsPage })),
);
const LinkSlackPage = lazy(() =>
  import("@/pages/LinkSlack").then((m) => ({ default: m.LinkSlackPage })),
);
const TrendAlertDetailPage = lazy(() =>
  import("@/pages/TrendAlertDetail").then((m) => ({
    default: m.TrendAlertDetailPage,
  })),
);
const StoryThemeDetailPage = lazy(() =>
  import("@/pages/StoryThemeDetail").then((m) => ({
    default: m.StoryThemeDetailPage,
  })),
);
// TRIAG-281 — deduplicated #user-feedback issues + its admin twin.
const FeedbackPage = lazy(() =>
  import("@/pages/Feedback").then((m) => ({ default: m.FeedbackPage })),
);
const AdminFeedbackPage = lazy(() =>
  import("@/pages/AdminFeedback").then((m) => ({
    default: m.AdminFeedbackPage,
  })),
);
// Users page (TRIAG-427) — app-user directory and profiles.
const OperatorPage = lazy(() =>
  import("@/pages/Operator").then((m) => ({ default: m.OperatorPage })),
);
const UsersPage = lazy(() =>
  import("@/pages/Users").then((m) => ({ default: m.UsersPage })),
);
const UserProfilePage = lazy(() =>
  import("@/pages/UserProfile").then((m) => ({ default: m.UserProfilePage })),
);
const SubscriptionsPage = lazy(() =>
  import("@/pages/Subscriptions").then((m) => ({
    default: m.SubscriptionsPage,
  })),
);
// Scheduled chat reports (SCHED v2, Galley #18690): reached from the DM's
// "manage" link; no TopNav entry, so nothing on an existing page moves.
// Chris's #20817: ask the triage chat from the dashboard. No nav entry.
const ChatPage = lazy(() =>
  import("@/pages/Chat").then((m) => ({
    default: m.ChatPage,
  })),
);
// Chris's #30404 package 2 (Galley #30412): one stored chat answer and the
// SQL behind it, linked from the Slack answer and from /chat. No nav entry.
const ChatAnswerPage = lazy(() =>
  import("@/pages/ChatAnswer").then((m) => ({
    default: m.ChatAnswerPage,
  })),
);
// Chris's #28574 (Galley #28616): reports over time, linked from the chat.
// No nav entry, so nothing on an existing page moves.
const ChartPage = lazy(() =>
  import("@/pages/ChartPage").then((m) => ({
    default: m.ChartPage,
  })),
);
const ScheduledReportsPage = lazy(() =>
  import("@/pages/ScheduledReports").then((m) => ({
    default: m.ScheduledReportsPage,
  })),
);
// Hardware page (TRIAG-429) — battery, heat, processor and memory across reports.
const HardwarePage = lazy(() =>
  import("@/pages/Hardware").then((m) => ({ default: m.HardwarePage })),
);

// ─── Query client ───────────────────────────────────────────────────────────

/**
 * Global TanStack Query client.
 *
 * Configured once at module scope so the instance survives across
 * hot module reloads in dev (Vite would otherwise give us a fresh
 * QueryClient per render, defeating the cache).
 *
 * Defaults:
 *
 * - `staleTime: 30_000` — queries are considered fresh for 30 seconds.
 *   Components mounting with a cached result within that window get
 *   an instant response with no refetch. Per-query overrides (e.g.
 *   `queries.session()` uses 60 s) take precedence.
 *
 * - `retry: 401/403-aware` — on auth failures, bail immediately.
 *   The `api<T>` fetch wrapper in `lib/api.ts` redirects to
 *   `/auth/google/login` on 401, so retrying would just log a
 *   console error race before the redirect completes. For
 *   non-auth failures, fall back to the default 3-retry policy.
 *
 * - `refetchOnWindowFocus: false` — the dashboard is consulted, not
 *   live-monitored. Refetching every time the user alt-tabs back
 *   would spam the backend for no user benefit. Operators who
 *   want fresh data can hit Cmd-R.
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        // Don't retry auth failures — the fetch wrapper in lib/api.ts
        // redirects to /auth/google/login on 401. Retrying after a
        // redirect has been initiated races the navigation and logs
        // a misleading "Network Error" to the console before the
        // page unloads.
        if (error instanceof Error && "status" in error) {
          const status = (error as unknown as { status: number }).status;
          if (status === 401 || status === 403) return false;
        }
        return failureCount < 3;
      },
    },
  },
});

// ─── Suspense fallback ──────────────────────────────────────────────────────

/**
 * Small centered "Loading…" indicator rendered while a lazy
 * route chunk is fetching. Matches the `AuthGuard` loading
 * fallback shape so transitions between "checking session" and
 * "loading page code" feel continuous — operators don't see a
 * flash of different layouts.
 */
function RouteLoadingFallback() {
  return (
    <div className="flex h-screen items-center justify-center bg-gray-50 dark:bg-gray-950">
      <div className="text-sm text-gray-500 dark:text-gray-400">Loading…</div>
    </div>
  );
}

// ─── App ────────────────────────────────────────────────────────────────────

/**
 * Zero-DOM component that installs the {@link useTrackPageView}
 * hook. Must live INSIDE `<BrowserRouter>` so `useLocation()` has
 * a router context to read. Rendered as a sibling of `<Routes>`
 * so it fires on every navigation without delaying route render.
 *
 * Added 2026-05-01 per operator directive to capture "every single
 * route, every time something is loaded, every user" — see
 * `dashboard/src/lib/tracking.ts` for the hook's fail-open
 * contract.
 */
function TelemetryInstaller(): null {
  useTrackPageView();
  return null;
}

/**
 * Zero-DOM component that keeps `document.title` in sync with the
 * route (UX P2, 2026-07-28: every tab used to read "Triage
 * Dashboard"). Same inside-BrowserRouter placement rationale as
 * {@link TelemetryInstaller}. The mapping is the pure, unit-tested
 * `titleForPath` in `dashboard/src/lib/page-title.ts`.
 */
function TitleInstaller(): null {
  const { pathname } = useLocation();
  useEffect(() => {
    document.title = titleForPath(pathname);
  }, [pathname]);
  return null;
}

/**
 * Zero-DOM component that resets the window scroll position on
 * forward navigations (UX P2, 2026-07-28: clicking Investigate on a
 * scrolled-down alert detail landed the family page mid-scroll,
 * worst on mobile where the pages are tall).
 *
 * Deliberately narrow:
 * - PUSH / REPLACE only — back/forward (POP) keeps the browser's own
 *   scroll restoration, so "← back to the list I was scanning" still
 *   returns to the right row.
 * - Skipped when the target carries a #hash — anchor-scrolling pages
 *   (e.g. /analyses/:uuid/conversation) own their scroll behavior.
 */
function ScrollToTopInstaller(): null {
  const { pathname, hash } = useLocation();
  const navigationType = useNavigationType();
  useEffect(() => {
    if (navigationType !== "POP" && !hash) {
      window.scrollTo(0, 0);
    }
  }, [pathname, hash, navigationType]);
  return null;
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      {/*
        2026-05-01 — ToastProvider wraps the router so every
        authenticated page (and its lazy-loaded children) can
        call `useToast()` for in-app success/error feedback.
        Copy-link buttons, future save-confirmation flows, and
        any operator action that wants transient feedback go
        through this provider's toast stack.

        Mounted OUTSIDE BrowserRouter so the toast-state is
        preserved across route changes — a toast fired on one
        page stays visible for the caller's requested duration
        even if the user navigates away before it auto-
        dismisses. (Router unmount would destroy the
        provider's React state if it lived inside the Routes.)
      */}
      <ToastProvider>
        {/*
          2026-05-01 late-evening — no KeyboardShortcutsProvider
          wrapper here. Operator decision: keyboard shortcuts are
          not part of this dashboard's UX surface. See the
          import-block comment above for the decision trail +
          CONTRIBUTING.md §"Rule: do NOT add keyboard shortcuts
          to the dashboard" for the rule a future contributor
          should consult before reintroducing them.
        */}
        <BrowserRouter>
          {/* Must render inside BrowserRouter so useLocation works. */}
          <TelemetryInstaller />
          {/* Galley #38663: "Triage is updating" while a deploy has the worker stopped;
              renders nothing otherwise. */}
          <UpdatingBanner />
          <TitleInstaller />
          <ScrollToTopInstaller />
          {/*
          One <Suspense> wrapping all routes rather than per-route.
          Per-route Suspense would be marginally more precise (the
          fallback would only replace the specific page, not the
          chrome) but we don't HAVE persistent chrome yet — every
          page renders its own header. A single top-level fallback
          is simpler and the UX is identical until we grow a
          shared PageShell.
        */}
          <Suspense fallback={<RouteLoadingFallback />}>
            {/*
              TRIAG-68 (2026-05-21) — global ErrorBoundary wraps
              <Routes> so any descendant render-time / lifecycle
              throw lands on a friendly fallback instead of the
              white-screen-of-death. Per-route boundaries below
              limit blast radius (a crash on /admin/llm doesn't
              clobber the global Suspense state); this outer
              boundary catches anything they miss (router itself,
              Suspense fallback rendering, lazy-import resolution).
              See `dashboard/src/components/ErrorBoundary.tsx`
              for the catches/doesn't-catch matrix and the logging
              contract.
            */}
            <ErrorBoundary name="App">
              {/* Galley #21943: a new time zone re-formats every time on
                  the page, keeping its state (components/TimeZoneRerender.tsx). */}
              <TimeZoneRerender>
              {() => (
              <Routes>
                {/*
              Unauthenticated route. LoginPage reads ?error=... from
              the query string to surface OAuth failure messages from
              the backend callback handler. Eager (no lazy) because
              it IS the first page an unauthenticated user lands on.
            */}
                <Route path="/login" element={<LoginPage />} />

                {/*
              Authenticated routes. AuthGuard is applied per-route
              (rather than as a layout wrapper) because v1 doesn't
              have a shared shell — each page renders its own header.
              When we add a shared PageShell, flip this to the
              layout-route pattern dk2 uses (one AuthGuard wrapping
              a <PageShell><Outlet/></PageShell> parent).
            */}

                {/* Home — eager (it's the landing target post-login). */}
                <Route
                  path="/"
                  element={
                    <AuthGuard>
                      <ErrorBoundary name="/">
                        <HomePage />
                      </ErrorBoundary>
                    </AuthGuard>
                  }
                />

                {/*
              2026-05-01: `/analyses` list route removed per operator
              directive — redundant with /bundles + /trends. The
              detail route below stays (Slack links, row-click
              drill-through, /duplicates navigation all target it).
              If a user with a stale bookmark hits `/analyses`, the
              catch-all route at the bottom sends them to `/` (the
              Home page then surfaces the alternative navigation).
            */}

                {/* Analysis detail (Wave 11). */}
                <Route
                  path="/analyses/:uuid"
                  element={
                    <AuthGuard>
                      <ErrorBoundary name="/analyses/:uuid">
                        <AnalysisDetailPage />
                      </ErrorBoundary>
                    </AuthGuard>
                  }
                />

                {/*
              Feature B (2026-05-01) — dedicated LLM conversation
              viewer. Reachable from AnalysisDetail's "Looking for
              more detail?" CTA strip + direct-link URLs shared in
              Slack / between operators. The `:uuid` path segment
              is the SAME UUID as the parent `/analyses/:uuid`
              page (bundle_lifecycle.analysis_id). Route order
              doesn't matter vs. the parent route — React Router
              picks the exact-match more-specific route.
            */}
                <Route
                  path="/analyses/:uuid/conversation"
                  element={
                    <AuthGuard>
                      <ErrorBoundary name="/analyses/:uuid/conversation">
                        <ConversationPage />
                      </ErrorBoundary>
                    </AuthGuard>
                  }
                />

                {/* Bundles list (Wave 11). */}
                <Route
                  path="/bundles"
                  element={
                    <AuthGuard>
                      <ErrorBoundary name="/bundles">
                        <BundlesPage />
                      </ErrorBoundary>
                    </AuthGuard>
                  }
                />

                {/* Search findings (Galley #33621): a new page; Bundles is unchanged. */}
                <Route
                  path="/search"
                  element={
                    <AuthGuard>
                      <ErrorBoundary name="/search">
                        <SearchFindingsPage />
                      </ErrorBoundary>
                    </AuthGuard>
                  }
                />

                {/* Bundle detail with live pipeline strip (Wave 11). */}
                <Route
                  path="/bundles/:uuid"
                  element={
                    <AuthGuard>
                      <ErrorBoundary name="/bundles/:uuid">
                        <BundleDetailPage />
                      </ErrorBoundary>
                    </AuthGuard>
                  }
                />

                {/* Recurring fixes (Galley #34714): a new page; Commits is unchanged. */}
                <Route
                  path="/fixes/recurring"
                  element={
                    <AuthGuard>
                      <ErrorBoundary name="/fixes/recurring">
                        <RecurringFixesPage />
                      </ErrorBoundary>
                    </AuthGuard>
                  }
                />

                {/* Problems (Galley #36384): a new page; Commits and Recurring fixes are unchanged. */}
                <Route
                  path="/fixes/problems"
                  element={
                    <AuthGuard>
                      <ErrorBoundary name="/fixes/problems">
                        <FixProblemsPage />
                      </ErrorBoundary>
                    </AuthGuard>
                  }
                />
                <Route
                  path="/fixes/problems/:id"
                  element={
                    <AuthGuard>
                      <ErrorBoundary name="/fixes/problems/:id">
                        <FixProblemDetailPage />
                      </ErrorBoundary>
                    </AuthGuard>
                  }
                />

                {/* TRIAG-312 — commit pre-flight review list + detail. */}
                <Route
                  path="/commits"
                  element={
                    <AuthGuard>
                      <ErrorBoundary name="/commits">
                        <CommitsPage />
                      </ErrorBoundary>
                    </AuthGuard>
                  }
                />
                <Route
                  path="/commits/:sha"
                  element={
                    <AuthGuard>
                      <ErrorBoundary name="/commits/:sha">
                        <CommitDetailPage />
                      </ErrorBoundary>
                    </AuthGuard>
                  }
                />

                {/* Duplicates detail — single dedup family (Wave 11). */}
                <Route
                  path="/duplicates/:canonicalUuid"
                  element={
                    <AuthGuard>
                      <ErrorBoundary name="/duplicates/:canonicalUuid">
                        <DuplicatesPage />
                      </ErrorBoundary>
                    </AuthGuard>
                  }
                />

                {/* Trends — top recurring dedup families (Wave 11). */}
                <Route
                  path="/alerts"
                  element={
                    <AuthGuard>
                      <ErrorBoundary name="/alerts">
                        <TrendAlertsPage />
                      </ErrorBoundary>
                    </AuthGuard>
                  }
                />
                <Route
                  path="/alerts/:id"
                  element={
                    <AuthGuard>
                      <ErrorBoundary name="/alerts/:id">
                        <TrendAlertDetailPage />
                      </ErrorBoundary>
                    </AuthGuard>
                  }
                />
                <Route
                  path="/trends"
                  element={
                    <AuthGuard>
                      <ErrorBoundary name="/trends">
                        <TrendsPage />
                      </ErrorBoundary>
                    </AuthGuard>
                  }
                />

                {/* Story-theme detail — shareable theme route
                    (TRIAG-331). Sits under /trends because the
                    stories tab is the parent view. */}
                <Route
                  path="/trends/stories/:themeId"
                  element={
                    <AuthGuard>
                      <ErrorBoundary name="/trends/stories/:themeId">
                        <StoryThemeDetailPage />
                      </ErrorBoundary>
                    </AuthGuard>
                  }
                />

                {/* TRIAG-342 — shake-cluster detail (same page,
                    source-parameterized: /api/shakes endpoints +
                    Shake Reports back-link + arrival-time caption). */}
                <Route
                  path="/trends/shakes/:themeId"
                  element={
                    <AuthGuard>
                      <ErrorBoundary name="/trends/shakes/:themeId">
                        <StoryThemeDetailPage source="shakes" />
                      </ErrorBoundary>
                    </AuthGuard>
                  }
                />

                {/* Remediations (Feature 05) is retired (Galley #20162, the
                    owner's #20375 and #20595): no /remediations route, so an
                    old link falls to the catch-all below and lands Home. The
                    API, its tables and every row stay. */}

                {/* Galley #26408: the Operator section (operators only; the
                    server re-checks the role on every /api/operator route). */}
                <Route
                  path="/operator"
                  element={
                    <AuthGuard>
                      <ErrorBoundary name="/operator">
                        <OperatorPage />
                      </ErrorBoundary>
                    </AuthGuard>
                  }
                />

                {/* Users page (TRIAG-427) — directory and profiles. ErrorBoundary
                    names are route PATTERNS, never a real username. */}
                <Route
                  path="/users"
                  element={
                    <AuthGuard>
                      <ErrorBoundary name="/users">
                        <UsersPage />
                      </ErrorBoundary>
                    </AuthGuard>
                  }
                />
                <Route
                  path="/users/:username"
                  element={
                    <AuthGuard>
                      <ErrorBoundary name="/users/:username">
                        <UserProfilePage />
                      </ErrorBoundary>
                    </AuthGuard>
                  }
                />
                <Route
                  path="/subscriptions"
                  element={
                    <AuthGuard>
                      <ErrorBoundary name="/subscriptions">
                        <SubscriptionsPage />
                      </ErrorBoundary>
                    </AuthGuard>
                  }
                />
                <Route
                  path="/scheduled-reports"
                  element={
                    <AuthGuard>
                      <ErrorBoundary name="/scheduled-reports">
                        <ScheduledReportsPage />
                      </ErrorBoundary>
                    </AuthGuard>
                  }
                />
                <Route
                  path="/chat"
                  element={
                    <AuthGuard>
                      <ErrorBoundary name="/chat">
                        <ChatPage />
                      </ErrorBoundary>
                    </AuthGuard>
                  }
                />
                <Route
                  path="/chat/answers/:id"
                  element={
                    <AuthGuard>
                      <ErrorBoundary name="/chat/answers/:id">
                        <ChatAnswerPage />
                      </ErrorBoundary>
                    </AuthGuard>
                  }
                />
                <Route
                  path="/chart"
                  element={
                    <AuthGuard>
                      <ErrorBoundary name="/chart">
                        <ChartPage />
                      </ErrorBoundary>
                    </AuthGuard>
                  }
                />

                {/* Hardware page (TRIAG-429). Flat route: platform, window,
                    grouping and filters are query params, never path
                    segments (page_views route templates). */}
                <Route
                  path="/hardware"
                  element={
                    <AuthGuard>
                      <ErrorBoundary name="/hardware">
                        <HardwarePage />
                      </ErrorBoundary>
                    </AuthGuard>
                  }
                />

                {/* Feedback — deduplicated #user-feedback issues
                    (TRIAG-281 Slice 3). */}
                <Route
                  path="/feedback"
                  element={
                    <AuthGuard>
                      <ErrorBoundary name="/feedback">
                        <FeedbackPage />
                      </ErrorBoundary>
                    </AuthGuard>
                  }
                />

                {/* Feedback-ingest health (TRIAG-281; admin-gated
                    server-side like /admin/usersnap). */}
                <Route
                  path="/admin/feedback"
                  element={
                    <AuthGuard>
                      <ErrorBoundary name="/admin/feedback">
                        <AdminFeedbackPage />
                      </ErrorBoundary>
                    </AuthGuard>
                  }
                />

                {/*
              Activity — system activity over time. Renamed from
              StatusPage in Wave 12 per Appendix D.9. The
              `/status` path redirects here below so old bookmarks
              and Slack links keep working.
            */}
                <Route
                  path="/activity"
                  element={
                    <AuthGuard>
                      <ErrorBoundary name="/activity">
                        <ActivityPage />
                      </ErrorBoundary>
                    </AuthGuard>
                  }
                />

                {/*
              Legacy `/status` path. Client-side redirect to
              `/activity` so old bookmarks + any cached Slack
              message with the old link keep working. `replace`
              means the history entry doesn't accumulate a bounce.
              Kept as an explicit route (not the catch-all) because
              the catch-all sends to `/` — we want `/status` to
              land on the actual Activity page.
            */}
                <Route
                  path="/status"
                  element={<Navigate to="/activity" replace />}
                />

                {/* `/triage link`: confirm the Slack ↔ login link (one-time code). */}
                <Route
                  path="/link/slack"
                  element={
                    <AuthGuard>
                      <ErrorBoundary name="/link/slack">
                        <LinkSlackPage />
                      </ErrorBoundary>
                    </AuthGuard>
                  }
                />

                {/* Admin — authenticated + admin-gated (Wave 11). */}
                <Route
                  path="/admin"
                  element={
                    <AuthGuard>
                      <ErrorBoundary name="/admin">
                        <AdminPage />
                      </ErrorBoundary>
                    </AuthGuard>
                  }
                />

                {/*
              Admin LLM observability — authenticated + admin-gated
              (2026-04-29). The server-side admin gate in every
              `/api/admin/llm/*` handler is the authoritative check;
              the AuthGuard here only ensures the session exists so
              the page can render its data-fetching cards. A non-
              admin user who navigates here sees the ForbiddenPanel
              component instead of the charts — no bundle of silent
              empty graphs.
            */}
                <Route
                  path="/admin/llm"
                  element={
                    <AuthGuard>
                      <ErrorBoundary name="/admin/llm">
                        <AdminLlmPage />
                      </ErrorBoundary>
                    </AuthGuard>
                  }
                />

                {/*
              Admin Usersnap ingest observability — authenticated
              + admin-gated (2026-04-29 TOP-OF-QUEUE #2). Same
              AuthGuard-only client-side gate as /admin/llm; the
              authoritative admin check is server-side in
              `require_admin`. Non-admin users see the
              ForbiddenPanel rather than bundle-of-empty-cards.
            */}
                <Route
                  path="/admin/usersnap"
                  element={
                    <AuthGuard>
                      <ErrorBoundary name="/admin/usersnap">
                        <AdminUsersnapPage />
                      </ErrorBoundary>
                    </AuthGuard>
                  }
                />

                {/*
              MCP server admin panel (2026-05-13 Phase 1). Setup
              walkthrough + per-operator session management.
              AuthGuard only — the endpoints scope to caller's
              own email server-side, so no admin gate needed.
            */}
                <Route
                  path="/admin/mcp"
                  element={
                    <AuthGuard>
                      <ErrorBoundary name="/admin/mcp">
                        <AdminMcpPage />
                      </ErrorBoundary>
                    </AuthGuard>
                  }
                />
                <Route
                  path="/admin/mcp/guide"
                  element={
                    <AuthGuard>
                      <ErrorBoundary name="/admin/mcp/guide">
                        <McpGuidePage />
                      </ErrorBoundary>
                    </AuthGuard>
                  }
                />

                {/*
              Admin dashboard-usage analytics (Priority 3 Phase 2,
              2026-05-06). AuthGuard-only client-side gate —
              the authoritative admin check is server-side in
              `require_admin` on every /api/admin/page-views/*
              endpoint. Non-admin users see the ForbiddenPanel
              rather than the bundle of empty cards.
            */}
                <Route
                  path="/admin/page-views"
                  element={
                    <AuthGuard>
                      <ErrorBoundary name="/admin/page-views">
                        <AdminPageViewsPage />
                      </ErrorBoundary>
                    </AuthGuard>
                  }
                />

                {/*
              Catch-all. Any unknown path sends the user home. This
              covers mistyped URLs, stale bookmarks pointing at
              removed routes, and the transient "empty" state between
              logout and the browser following the redirect to
              /login. The AuthGuard on `/` handles the "no session"
              case, so an unauthenticated user hitting an unknown
              path lands on /login after one bounce.
            */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
              )}
              </TimeZoneRerender>
            </ErrorBoundary>
          </Suspense>
        </BrowserRouter>
      </ToastProvider>
    </QueryClientProvider>
  );
}
